import {Injectable} from '@angular/core'
import {
  JobRequestDamage,
  JobRequestExecutionDetails,
  JobRequestPreferredPro,
  JobRequestRepository,
  JobRequestStakeholder,
  JobRequestState,
} from './job-request.repository'
import {map} from 'rxjs/operators'
import {LinkService} from '../../../core/i18n/link.service'
import {pickProperty} from '../../../core/graphql/graphql-utils'
import {
  AgentExpectation,
  DamageInput,
  JobRequest_ClientLastJobFragment,
  JobRequest_GetClientLastJobGql,
  JobRequestInput,
  JobRequestResultFragment,
  RequestDamageJobGql,
  RequestJobForAgencyGql,
  RequestJobForPreferredProGql,
  RequestJobGql,
} from '../../../../__generated__/types'
import {Observable, of} from 'rxjs'
import {ErrorMonitor} from '../../../core/error/error-monitoring.types'
import {Logger} from '../../../core/logging/logger.types'
import {AuthStatusService} from '../../../core/auth/auth-status.service'

@Injectable()
export class JobRequestService {
  private startPagePath?: string
  constructor(
    private readonly repository: JobRequestRepository,
    private readonly linkService: LinkService,
    private readonly errorMonitor: ErrorMonitor,
    private readonly authStatusService: AuthStatusService,
    private readonly requestJobGql: RequestJobGql,
    private readonly requestDamageJobGql: RequestDamageJobGql,
    private readonly requestJobForAgencyGql: RequestJobForAgencyGql,
    private readonly requestJobForPreferredProGql: RequestJobForPreferredProGql,
    private readonly getClientLastJobGql: JobRequest_GetClientLastJobGql,
    private readonly logger: Logger,
  ) {}

  requestProcessDidStart(offeringId?: string) {
    this.startPagePath = this.linkService.previousPage
    const previousState = this.repository.readJobRequestOfferingState()
    const matchPreviousOffering = previousState?.id === offeringId

    // if the new request process starts for a different offering, clear non reusable state (question answers, title, description, etc)
    if (!matchPreviousOffering) {
      this.repository.clearNonReusableRequestState()
    }
  }

  canSkipExecutionDateQuestion(state: {execution?: Partial<JobRequestExecutionDetails>}) {
    return state.execution?.skipExecutionDate ?? false
  }

  isOwnershipQuestionOptional(state: {damage?: JobRequestDamage}) {
    const isDamageRequest = !!state.damage
    return isDamageRequest
  }

  shouldAskClientExpectation(state: {offering?: {showClientExpectation: boolean}; damage?: JobRequestDamage}): boolean {
    const isDamageRequest = !!state.damage
    if (isDamageRequest) {
      return false
    } else {
      return state.offering?.showClientExpectation ?? false
    }
  }

  shouldAskAgentExpectation(state: {damage?: JobRequestDamage}): boolean {
    const isDamageRequest = !!state.damage
    return isDamageRequest
  }

  canSkipTermsAndConditions(state: {damage?: JobRequestDamage}): boolean {
    const isDamageRequest = !!state.damage
    return isDamageRequest
  }

  referrerPage(): string | undefined {
    // maybe the user hasn't yet finished first step (where we store the state referrer), use this as the safest value
    if (this.startPagePath) {
      return this.startPagePath
    }
    // maybe a page refresh occurred, this is a persisted value
    const state = this.repository.readJobRequestDetailState()
    if (state) {
      return state.referrer
    }
  }

  /**
   * Returns a client's last job
   * @note this method won't wait for authentication status if not yet resolved
   */
  getClientLastJob(): Observable<ClientLastJob | undefined> {
    const me = this.authStatusService.me()
    if (!me?.client) {
      return of(undefined)
    }
    return this.getClientLastJobGql.fetch({clientId: me.client.id}, {fetchPolicy: 'cache-first'}).pipe(
      map(res => pickProperty(res.data, 'searchClientJobs')),
      map(res => res.data[0]),
    )
  }

  requestJob(state: JobRequestState): Observable<JobRequestResultFragment> {
    const jobRequest = this.convertToJobRequestInput(state)
    const damage = state.damage

    // coherence check for damage jobs, as data comes from different places
    if (damage && !state.damageAgentExpectation) {
      const err = 'Incoherent job request state: damage is defined but damageAgentExpectation is undefined'
      this.logger.error(err)
      this.errorMonitor.captureException(new Error(err))
    }

    if (damage && state.damageAgentExpectation) {
      return this.requestDamageJob(jobRequest, this.convertToDamageInput(damage, state.damageAgentExpectation))
    } else if (state.stakeholder) {
      return this.requestJobForAgency(jobRequest, state.stakeholder)
    } else if (state.preferredPro) {
      return this.requestJobForPreferredPro(jobRequest, state.preferredPro)
    } else {
      return this.requestRegularJob(jobRequest)
    }
  }

  private requestRegularJob(jobRequest: JobRequestInput): Observable<JobRequestResultFragment> {
    return this.requestJobGql.mutate({jobRequest}).pipe(map(res => pickProperty(res.data, 'requestJob')))
  }

  private requestDamageJob(jobRequest: JobRequestInput, damage: DamageInput): Observable<JobRequestResultFragment> {
    return this.requestDamageJobGql
      .mutate({jobRequest, damage})
      .pipe(map(res => pickProperty(res.data, 'requestDamageJob')))
  }

  private requestJobForAgency(
    jobRequest: JobRequestInput,
    stakeholder: JobRequestStakeholder,
  ): Observable<JobRequestResultFragment> {
    const {agencyJobRequestCode} = stakeholder
    return this.requestJobForAgencyGql
      .mutate({jobRequest, agencyJobRequestCode})
      .pipe(map(res => pickProperty(res.data, 'requestJobForAgency')))
  }

  private requestJobForPreferredPro(
    jobRequest: JobRequestInput,
    pro: JobRequestPreferredPro,
  ): Observable<JobRequestResultFragment> {
    const {proId} = pro
    return this.requestJobForPreferredProGql
      .mutate({jobRequest, proId})
      .pipe(map(res => pickProperty(res.data, 'requestJobForPreferredPro')))
  }

  private convertToJobRequestInput(state: JobRequestState): JobRequestInput {
    const {contact, detail, executionDetails, billingDetails} = state
    const {executionAddress} = executionDetails
    if (!contact || !detail) {
      throw new RangeError(`Invalid job request state ! Missing contact information`)
    }
    if (!detail) {
      throw new RangeError(`Invalid job request state ! Missing job details`)
    }

    const jobRequestInput: JobRequestInput = {
      client: contact,
      title: detail.title,
      description: detail.description,
      executionAddress: executionAddress
        ? {
            floor: executionDetails.floor,
            locality: executionAddress.locality,
            postalCode: this.parsePostalCode(executionAddress.postalCode),
            streetAndHouseNumber: executionAddress.streetAndHouseNumber,
            ownership: executionDetails.ownership,
          }
        : undefined,
      executionDate: {
        expectedExecution: state.executionDetails.expectedExecution,
        latestPossibleDate: state.executionDetails.latestPossibleDate,
      },
      fileKeys: state.attachedFiles?.files?.map(f => f.fileKey),
      offering: state.offering,
      referrer: detail.referrer,
      voucherCode: detail.voucherCode,
      clientExpectation: state.clientExpectation,
      analyticParams: this.mapAnalyticParams(state.analyticParams),
    }

    if (billingDetails.billingAddressNotIdentical && billingDetails.billingAddress && billingDetails.billingClient) {
      jobRequestInput.billingClient = billingDetails.billingClient
      jobRequestInput.billingAddress = {
        ...billingDetails.billingAddress,
        postalCode: this.parsePostalCode(billingDetails.billingAddress.postalCode),
      }
    }

    return jobRequestInput
  }

  private convertToDamageInput(state: JobRequestDamage, agentExpectation: AgentExpectation): DamageInput {
    return {
      caseNumber: state.caseNumber,
      occurredAt: state.occurredAt,
      agentJobRequestCode: state.agentJobRequestCode,
      agentExpectation: {
        offerValidationType: agentExpectation.offerValidationType,
        deductible: agentExpectation.deductible,
        coverage: agentExpectation.coverage,
        directives: state.agentDirectives,
      },
    }
  }

  private parsePostalCode(postalCode: number | string): number {
    if (Number.isInteger(postalCode)) {
      return postalCode as number
    }

    return Number.parseInt(postalCode as string)
  }

  private mapAnalyticParams(analyticParams: Record<string, string> | undefined) {
    if (analyticParams) {
      return Object.entries(analyticParams).map(x => ({name: x[0], value: x[1]}))
    }
    return analyticParams
  }
}

export type ClientLastJob = JobRequest_ClientLastJobFragment
